import { http } from "../helpers/easyindustriaapi/config";

export default class SchedulingRequestController {
    constructor(user, empresa) {
        this.currentUser = user || JSON.parse(localStorage.getItem('user'));
        this.currentEmpresa = empresa || JSON.parse(localStorage.getItem('currentEmpresa'));
    }

    /**
     * Lista todas as solicitações de reagendamento
     * GET /scheduling-request
     * 
     * @param {Object} params - Parâmetros de filtro
     * @param {number} params.professional_id - ID do profissional
     * @param {string} params.data_inicio - Data inicial (formato: YYYY-MM-DD)
     * @param {string} params.data_fim - Data final (formato: YYYY-MM-DD)
     * @param {string} params.status - Status da solicitação (pending, approved, rejected, processed)
     * @param {number} params.atendimento_id - ID do atendimento
     * @returns {Promise} Response da API
     */
    async getSchedulingRequests(params = {}) {
        try {
            const queryParams = {
                empresa_id: this.currentEmpresa?.id,
                ...params
            };

            // Remove parâmetros nulos ou undefined
            Object.keys(queryParams).forEach(key => {
                if (queryParams[key] === null || queryParams[key] === undefined) {
                    delete queryParams[key];
                }
            });

            const response = await http.get('/scheduling-request', { params: queryParams });
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar solicitações de reagendamento:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Busca uma solicitação específica por ID
     * GET /scheduling-request/{id}
     * 
     * @param {number} id - ID da solicitação
     * @returns {Promise} Response da API
     */
    async getSchedulingRequest(id) {
        try {
            const response = await http.get(`/scheduling-request/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar solicitação de reagendamento:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Cria uma nova solicitação de reagendamento
     * POST /scheduling-request
     * 
     * @param {Object} data - Dados da solicitação
     * @param {number} data.atendimento_id - ID do atendimento
     * @param {number} data.empresa_id - ID da empresa
     * @param {string} data.old_date_end - Data de término da agenda atual (YYYY-MM-DD)
     * @param {string} data.new_date_start - Nova data de início (YYYY-MM-DD)
     * @param {string} data.new_time_start - Nova hora de início (HH:mm)
     * @param {string} data.new_time_end - Nova hora de término (HH:mm)
     * @returns {Promise} Response da API
     */
    async createRequest(data) {
        try {
            const payload = {
                atendimento_id: data.atendimento_id,
                empresa_id: data.empresa_id || this.currentEmpresa?.id,
                old_date_end: data.old_date_end,
                new_date_start: data.new_date_start,
                new_time_start: data.new_time_start,
                new_time_end: data.new_time_end
            };

            const response = await http.post('/scheduling-request', payload);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar solicitação de reagendamento:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Aprova uma solicitação de reagendamento
     * PUT /scheduling-request/{id}/approve
     * 
     * @param {number} id - ID da solicitação
     * @returns {Promise} Response da API
     */
    async approveRequest(id) {
        try {
            const payload = {
                id: id
            };

            const response = await http.put(`/scheduling-request/${id}/approve`, payload);
            return response.data;
        } catch (error) {
            console.error('Erro ao aprovar solicitação de reagendamento:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Rejeita uma solicitação de reagendamento
     * PUT /scheduling-request/{id}/reject
     * 
     * @param {number} id - ID da solicitação
     * @param {string} rejectionReason - Motivo da rejeição
     * @returns {Promise} Response da API
     */
    async rejectRequest(id, rejectionReason) {
        try {
            const payload = {
                id: id,
                rejection_reason: rejectionReason
            };

            const response = await http.put(`/scheduling-request/${id}/reject`, payload);
            return response.data;
        } catch (error) {
            console.error('Erro ao rejeitar solicitação de reagendamento:', error);
            throw this.handleError(error);
        }
    }

    /**
     * Trata erros da API de forma padronizada
     * 
     * @param {Error} error - Erro capturado
     * @returns {Error} Erro formatado
     */
    handleError(error) {
        let messageErro = "";

        // error.response
        if (error.response && error.response.data) {
            if (typeof error.response.data === 'string') {
                messageErro = error.response.data;
            } else if (error.response.data.message) {
                messageErro = error.response.data.message;
            } else if (error.response.data.error) {
                messageErro = error.response.data.error;
            } else {
                messageErro = JSON.stringify(error.response.data);
            }
        }

        switch (error.response ? error.response.status : null) {
            case 400:
                return new Error('Erro 400: ' + messageErro || 'Requisição inválida');
            case 401:
                return new Error('Erro 401: Não autorizado');
            case 403:
                return new Error('Erro 403: ' + messageErro || 'Acesso negado');
            case 404:
                return new Error('Erro 404: ' + messageErro || 'Recurso não encontrado');
            case 406:
                return new Error('Erro 406: ' + messageErro);
            case 422:
                // Erro de validação
                if (error.response.data.errors) {
                    const errors = Object.values(error.response.data.errors).flat();
                    return new Error(errors.join(', '));
                }
                return new Error('Erro 422: ' + messageErro || 'Dados inválidos');
            case 500:
                return new Error('Erro 500: ' + messageErro || 'Erro interno do servidor');
            default:
                return new Error(messageErro || error.message || 'Erro desconhecido');
        }
    }
}

